import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import type { SavedOrder } from "@/hooks/useOrders";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import WhatsAppFloat from "@/pages/home/components/WhatsAppFloat";
import AnnouncementBar from "@/pages/home/components/AnnouncementBar";
import OrderTimeline from "./components/OrderTimeline";
import OrderItems from "./components/OrderItems";
import OrderSummaryCard from "./components/OrderSummaryCard";
import OrderDeliveryInfo from "./components/OrderDeliveryInfo";
import OrderHeader from "./components/OrderHeader";
import BankTransferStatus from "./components/BankTransferStatus";
import ReviewOrderModal from "./components/ReviewOrderModal";
import OrderReturns from "./components/OrderReturns";

export interface TimelineStep {
  status: string;
  label: string;
  date: string;
  description: string;
  icon: string;
  active: boolean;
  completed: boolean;
}

export interface RedeemCouponInfo {
  couponCode: string;
  discountPercent: number;
  discountAmount: number;
  status: string;
  usedAt: string | null;
}

export interface OrderWithTimeline extends SavedOrder {
  timeline: TimelineStep[];
  redeemCoupon?: RedeemCouponInfo;
  pointsEarned?: number;
}

export const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; border: string; icon: string; step: number }> = {
  pending:   { label: "قيد المعالجة", color: "text-amber-400",  bg: "bg-amber-400/10",  border: "border-amber-400/20",  icon: "ri-time-line",             step: 0 },
  confirmed: { label: "تم التأكيد",   color: "text-green-400",  bg: "bg-green-400/10",  border: "border-green-400/20",  icon: "ri-checkbox-circle-line",  step: 1 },
  shipped:   { label: "تم الشحن",     color: "text-sky-400",    bg: "bg-sky-400/10",    border: "border-sky-400/20",    icon: "ri-send-plane-line",       step: 2 },
  delivered: { label: "تم التوصيل",   color: "text-teal-400",   bg: "bg-teal-400/10",   border: "border-teal-400/20",   icon: "ri-truck-line",            step: 3 },
  cancelled: { label: "ملغي",         color: "text-red-400",    bg: "bg-red-400/10",    border: "border-red-400/20",    icon: "ri-close-circle-line",     step: -1 },
};

export function buildTimeline(order: SavedOrder): TimelineStep[] {
  const currentStep = STATUS_CONFIG[order.status]?.step ?? 0;
  const baseDate = new Date(order.date);

  return [
    {
      status: "pending",
      label: "تم استلام الطلب",
      date: order.date,
      description: "طلبك قيد المراجعة وسنتواصل معك قريباً",
      icon: "ri-shopping-bag-3-line",
      active: currentStep === 0,
      completed: currentStep >= 0,
    },
    {
      status: "confirmed",
      label: "تم تأكيد الطلب",
      date: new Date(baseDate.getTime() + 2 * 60 * 60 * 1000).toISOString(),
      description: "تم التحقق من توفر المنتجات وجاهزية الطلب للشحن",
      icon: "ri-checkbox-circle-line",
      active: currentStep === 1,
      completed: currentStep >= 1,
    },
    {
      status: "shipped",
      label: "في الطريق إليك",
      date: new Date(baseDate.getTime() + 24 * 60 * 60 * 1000).toISOString(),
      description: "طلبك انطلق مع شركة الشحن وفي طريقه إليك",
      icon: "ri-send-plane-line",
      active: currentStep === 2,
      completed: currentStep >= 2,
    },
    {
      status: "delivered",
      label: "تم التوصيل",
      date: new Date(baseDate.getTime() + 72 * 60 * 60 * 1000).toISOString(),
      description: "وصل طلبك بنجاح — شكراً لتسوقك معنا!",
      icon: "ri-gift-line",
      active: currentStep === 3,
      completed: currentStep >= 3,
    },
  ];
}

export default function OrderDetailPage() {
  const { ref } = useParams<{ ref: string }>();
  const navigate = useNavigate();
  const { user, isLoggedIn } = useAuth();
  const [barVisible, setBarVisible] = useState(true);
  const handleBarVisibility = useCallback((v: boolean) => setBarVisible(v), []);

  const [order, setOrder] = useState<OrderWithTimeline | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showReviewModal, setShowReviewModal] = useState(false);

  // Cancel order states
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);

  useEffect(() => {
    if (!ref) {
      setError("رقم الطلب غير صحيح");
      setLoading(false);
      return;
    }

    const fetchOrder = async () => {
      setLoading(true);
      try {
        const { data, error: fetchError } = await supabase
          .from("orders")
          .select("*")
          .eq("order_ref", ref)
          .maybeSingle();

        if (fetchError || !data) {
          setError("لم يتم العثور على الطلب");
          setLoading(false);
          return;
        }

        if (isLoggedIn && user) {
          if (data.phone !== user.phone && data.customer_id !== user.id) {
            setError("لا يمكنك الوصول لهذا الطلب");
            setLoading(false);
            return;
          }
        }

        const orderData: SavedOrder = {
          orderRef: String(data.order_ref),
          date: String(data.created_at),
          name: String(data.name),
          phone: String(data.phone),
          city: String(data.city),
          address: String(data.address),
          notes: String(data.notes ?? ""),
          paymentLabel: String(data.payment_label),
          items: Array.isArray(data.items) ? data.items : [],
          totalPrice: Number(data.total_price),
          discountCode: String(data.discount_code ?? ""),
          discountPercent: Number(data.discount_percent ?? 0),
          discountAmount: Number(data.discount_amount ?? 0),
          finalPrice: Number(data.final_price),
          status: data.status as SavedOrder["status"],
          customerId: data.customer_id ? String(data.customer_id) : undefined,
        };

        // Fetch redeem coupon used in this order
        let redeemCoupon: RedeemCouponInfo | undefined;
        if (data.customer_id) {
          const { data: couponData } = await supabase
            .from("redeem_coupons")
            .select("coupon_code, discount_percent, status, used_at")
            .eq("customer_id", data.customer_id)
            .eq("status", "used")
            .order("used_at", { ascending: false })
            .limit(5);

          // Match coupon used around order time (within 5 minutes)
          const orderTime = new Date(data.created_at).getTime();
          const matched = (couponData ?? []).find((c) => {
            if (!c.used_at) return false;
            const diff = Math.abs(new Date(c.used_at).getTime() - orderTime);
            return diff < 5 * 60 * 1000;
          });

          if (matched) {
            const discountAmt = Math.round(Number(data.total_price) * (matched.discount_percent / 100));
            redeemCoupon = {
              couponCode: matched.coupon_code,
              discountPercent: matched.discount_percent,
              discountAmount: discountAmt,
              status: matched.status,
              usedAt: matched.used_at,
            };
          }
        }

        // Points earned
        const pointsEarned = data.points_earned ? Number(data.points_earned) : undefined;

        setOrder({ ...orderData, timeline: buildTimeline(orderData), redeemCoupon, pointsEarned });
      } catch {
        setError("حدث خطأ أثناء تحميل الطلب");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [ref, isLoggedIn, user]);

  const handleCancelOrder = useCallback(async () => {
    if (!order) return;
    setCancelling(true);
    try {
      const { error: updateError } = await supabase
        .from("orders")
        .update({ status: "cancelled" })
        .eq("order_ref", order.orderRef);

      if (updateError) throw updateError;

      setOrder((prev) =>
        prev
          ? {
              ...prev,
              status: "cancelled",
              timeline: buildTimeline({ ...prev, status: "cancelled" }),
            }
          : prev
      );
      setShowCancelConfirm(false);
      setCancelSuccess(true);
      setTimeout(() => setCancelSuccess(false), 4000);
    } catch {
      // silently fail — user can retry
    } finally {
      setCancelling(false);
    }
  }, [order]);

  return (
    <div dir="rtl" className="bg-[#0a0a0a] min-h-screen">
      <AnnouncementBar onVisibilityChange={handleBarVisibility} />
      <Navbar barVisible={barVisible} />

      {/* Cancel Success Toast */}
      {cancelSuccess && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-red-500/90 text-white text-sm font-bold px-5 py-3 rounded-2xl flex items-center gap-2 shadow-lg" style={{ fontFamily: "'Cairo', sans-serif" }}>
          <i className="ri-close-circle-line text-base"></i>
          تم إلغاء طلبك بنجاح
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && order && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-[#141414] border border-red-400/20 rounded-2xl p-6 max-w-sm w-full shadow-2xl" style={{ fontFamily: "'Cairo', sans-serif" }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-400/10 border border-red-400/20 rounded-xl flex-shrink-0">
                <i className="ri-error-warning-line text-red-400 text-2xl"></i>
              </div>
              <div>
                <p className="text-white font-black text-sm">تأكيد إلغاء الطلب</p>
                <p className="text-gray-500 text-xs mt-0.5">#{order.orderRef}</p>
              </div>
            </div>

            <p className="text-gray-400 text-sm leading-relaxed mb-5">
              هل أنت متأكد من إلغاء هذا الطلب؟ لا يمكن التراجع عن هذا الإجراء.
            </p>

            <div className="bg-red-400/5 border border-red-400/15 rounded-xl px-4 py-3 mb-5">
              <p className="text-red-400/80 text-xs flex items-start gap-2">
                <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
                يمكن إلغاء الطلب فقط قبل تأكيده من فريقنا. للاستفسار بعد الإلغاء تواصل معنا عبر واتساب.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleCancelOrder}
                disabled={cancelling}
                className="flex-1 flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white font-black py-3 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
              >
                {cancelling ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>جاري الإلغاء...</>
                ) : (
                  <><i className="ri-close-circle-line text-base"></i>نعم، ألغِ الطلب</>
                )}
              </button>
              <button
                onClick={() => setShowCancelConfirm(false)}
                disabled={cancelling}
                className="flex-1 bg-[#1a1a1a] hover:bg-[#222] border border-white/8 text-gray-300 font-bold py-3 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
              >
                تراجع
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="pt-24 pb-20 px-4">
        <div className="max-w-3xl mx-auto">

          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-xs mb-8">
            <Link to="/" className="text-gray-600 hover:text-gray-400 transition-colors">المتجر</Link>
            <i className="ri-arrow-left-s-line text-gray-700"></i>
            {isLoggedIn && (
              <>
                <Link to="/account" className="text-gray-600 hover:text-gray-400 transition-colors">حسابي</Link>
                <i className="ri-arrow-left-s-line text-gray-700"></i>
              </>
            )}
            <span className="text-gray-400">تفاصيل الطلب</span>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-32 gap-4">
              <div className="relative w-16 h-16">
                <div className="absolute inset-0 border-2 border-amber-400/20 rounded-full"></div>
                <div className="absolute inset-0 border-2 border-transparent border-t-amber-400 rounded-full animate-spin"></div>
                <div className="absolute inset-3 flex items-center justify-center">
                  <i className="ri-box-3-line text-amber-400 text-lg"></i>
                </div>
              </div>
              <p className="text-gray-500 text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
                جاري تحميل تفاصيل الطلب...
              </p>
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="flex flex-col items-center justify-center py-32 text-center gap-5">
              <div className="w-24 h-24 flex items-center justify-center bg-red-400/5 border border-red-400/10 rounded-3xl">
                <i className="ri-error-warning-line text-4xl text-red-400/60"></i>
              </div>
              <div>
                <h2 className="text-white font-bold text-lg mb-1" style={{ fontFamily: "'Cairo', sans-serif" }}>{error}</h2>
                <p className="text-gray-500 text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>تحقق من رقم الطلب وحاول مجدداً</p>
              </div>
              <button
                onClick={() => navigate(isLoggedIn ? "/account" : "/")}
                className="px-6 py-3 bg-amber-400 hover:bg-amber-500 text-black font-bold rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                {isLoggedIn ? "العودة للحساب" : "العودة للمتجر"}
              </button>
            </div>
          )}

          {/* Order Content */}
          {!loading && !error && order && (
            <div className="space-y-5">
              <OrderHeader order={order} />
              <BankTransferStatus order={order} />
              <OrderTimeline order={order} />
              <OrderItems order={order} />

              {/* Returns section */}
              <OrderReturns
                orderRef={order.orderRef}
                customerName={order.name}
                customerPhone={order.phone}
                orderItems={order.items.map((i) => ({ nameAr: i.nameAr }))}
                orderStatus={order.status}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <OrderSummaryCard order={order} />
                <OrderDeliveryInfo order={order} />
              </div>

              {/* Points earned card — delivered orders */}
              {order.status === "delivered" && order.pointsEarned && order.pointsEarned > 0 && (
                <div className="bg-gradient-to-l from-amber-400/5 to-amber-400/10 border border-amber-400/20 rounded-2xl p-5 flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-400/15 border border-amber-400/25 rounded-xl flex-shrink-0">
                    <i className="ri-vip-crown-line text-amber-400 text-2xl"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-black text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
                      تم إضافة نقاطك!
                    </p>
                    <p className="text-amber-400/80 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                      كسبت <span className="font-black text-amber-400 text-base">+{order.pointsEarned}</span> نقطة مكافأة من هذا الطلب
                    </p>
                  </div>
                  <a
                    href="/rewards"
                    className="flex items-center gap-1.5 bg-amber-400/15 hover:bg-amber-400/25 border border-amber-400/25 text-amber-400 font-bold text-xs px-4 py-2.5 rounded-xl transition-all cursor-pointer whitespace-nowrap"
                    style={{ fontFamily: "'Cairo', sans-serif" }}
                  >
                    <i className="ri-coin-line text-sm"></i>
                    عرض نقاطي
                  </a>
                </div>
              )}

              {/* Redeem coupon used card */}
              {order.redeemCoupon && (
                <div className="bg-[#141414] border border-amber-400/15 rounded-2xl p-5">
                  <h3 className="text-white font-bold text-sm mb-4 flex items-center gap-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    <div className="w-7 h-7 flex items-center justify-center bg-amber-400/10 rounded-lg">
                      <i className="ri-exchange-dollar-line text-amber-400 text-sm"></i>
                    </div>
                    كوبون استبدال النقاط المستخدم
                  </h3>
                  <div className="bg-[#1a1a1a] border border-amber-400/20 rounded-xl overflow-hidden">
                    {/* Ticket style */}
                    <div className="flex items-stretch">
                      <div className="w-16 flex-shrink-0 flex flex-col items-center justify-center p-3 bg-amber-400/10 border-l border-dashed border-amber-400/20">
                        <p className="text-2xl font-black text-amber-400 leading-none" style={{ fontFamily: "'Cairo', sans-serif" }}>
                          {order.redeemCoupon.discountPercent}%
                        </p>
                        <p className="text-[10px] text-amber-400/60 mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>خصم</p>
                      </div>
                      <div className="flex-1 p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-700 text-gray-400" style={{ fontFamily: "'Cairo', sans-serif" }}>
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-500 inline-block"></span>
                            مستخدم
                          </span>
                        </div>
                        <p className="font-mono text-sm font-black text-white tracking-widest">{order.redeemCoupon.couponCode}</p>
                        <div className="flex items-center gap-3 mt-2 flex-wrap">
                          <span className="text-[11px] text-gray-500 flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                            <i className="ri-price-tag-3-line text-xs"></i>
                            وفّر {order.redeemCoupon.discountAmount.toLocaleString()} ر.س
                          </span>
                          {order.redeemCoupon.usedAt && (
                            <span className="text-[11px] text-gray-600 flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                              <i className="ri-check-double-line text-xs"></i>
                              استُخدم: {new Date(order.redeemCoupon.usedAt).toLocaleDateString("ar-SA", { month: "short", day: "numeric" })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <i className="ri-information-line text-gray-600 text-xs"></i>
                    <p className="text-gray-600 text-[11px]" style={{ fontFamily: "'Cairo', sans-serif" }}>
                      يمكنك استبدال نقاطك بكوبونات جديدة من
                      <a href="/redeem" className="text-amber-400/70 hover:text-amber-400 mr-1 transition-colors cursor-pointer">صفحة الاستبدال</a>
                    </p>
                  </div>
                </div>
              )}

              {/* Review CTA — only for delivered orders */}
              {order.status === "delivered" && (
                <div className="bg-gradient-to-l from-amber-400/5 to-amber-400/10 border border-amber-400/20 rounded-2xl p-5 flex flex-col sm:flex-row items-center gap-4">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-12 h-12 flex items-center justify-center bg-amber-400/15 rounded-xl flex-shrink-0">
                      <i className="ri-star-smile-line text-amber-400 text-2xl"></i>
                    </div>
                    <div>
                      <p className="text-white font-black text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
                        كيف كانت تجربتك؟
                      </p>
                      <p className="text-gray-400 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                        قيّم المنتجات وساعد العملاء الآخرين على الاختيار
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowReviewModal(true)}
                    className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-black font-black px-6 py-3 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
                    style={{ fontFamily: "'Cairo', sans-serif" }}
                  >
                    <i className="ri-star-line text-base"></i>
                    قيّم طلبك
                  </button>
                </div>
              )}

              {/* Cancel Order CTA — only for pending orders */}
              {order.status === "pending" && (
                <div className="bg-red-400/5 border border-red-400/15 rounded-2xl p-5 flex flex-col sm:flex-row items-center gap-4">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-12 h-12 flex items-center justify-center bg-red-400/10 border border-red-400/15 rounded-xl flex-shrink-0">
                      <i className="ri-close-circle-line text-red-400 text-2xl"></i>
                    </div>
                    <div>
                      <p className="text-white font-black text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
                        تريد إلغاء الطلب؟
                      </p>
                      <p className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                        يمكن الإلغاء فقط قبل تأكيد الطلب من فريقنا
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowCancelConfirm(true)}
                    className="flex items-center gap-2 bg-red-500/15 hover:bg-red-500/25 border border-red-500/30 hover:border-red-500/50 text-red-400 font-black px-6 py-3 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
                    style={{ fontFamily: "'Cairo', sans-serif" }}
                  >
                    <i className="ri-close-line text-base"></i>
                    إلغاء الطلب
                  </button>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3 pt-1">
                <a
                  href={`https://wa.me/966541138847?text=${encodeURIComponent(`استفسار عن طلب رقم: ${order.orderRef}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 bg-[#25D366]/10 hover:bg-[#25D366]/20 border border-[#25D366]/30 text-[#25D366] font-bold py-3.5 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                >
                  <i className="ri-whatsapp-line text-base"></i>
                  استفسار عبر واتساب
                </a>
                {isLoggedIn ? (
                  <Link
                    to="/account"
                    className="flex-1 flex items-center justify-center gap-2 bg-[#141414] hover:bg-[#1a1a1a] border border-white/8 text-gray-300 font-bold py-3.5 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
                    style={{ fontFamily: "'Cairo', sans-serif" }}
                  >
                    <i className="ri-arrow-right-line text-base"></i>
                    العودة للحساب
                  </Link>
                ) : (
                  <Link
                    to="/products"
                    className="flex-1 flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-500 text-black font-bold py-3.5 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
                    style={{ fontFamily: "'Cairo', sans-serif" }}
                  >
                    <i className="ri-store-2-line text-base"></i>
                    تسوق المزيد
                  </Link>
                )}
              </div>

              {/* Review Modal */}
              {showReviewModal && (
                <ReviewOrderModal
                  order={order}
                  onClose={() => setShowReviewModal(false)}
                />
              )}
            </div>
          )}
        </div>
      </main>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
